import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { OwlApi } from "./api";
import {
  clearSession,
  readSession,
  writeSession,
  hasBeenPrompted,
  markPrompted,
  type OwlSession,
} from "./session";

/**
 * Single source of truth for "who is using Owl right now".
 *
 * - The provider reads localStorage on mount; if a token is present it
 *   hits /me to verify it's still valid.
 * - identify(email) calls the API, stores the new token, updates state.
 * - signOut() invalidates server-side and clears local state.
 *
 * Components subscribe via `useSession()`. The shape is intentionally
 * narrow: status + email + queryCount + actions. No reducers, no
 * external state library.
 */

type Status = "loading" | "anonymous" | "signed_in";

interface SessionState {
  status: Status;
  email: string | null;
  queryCount: number;
  /** Has the first-visit Identify modal been shown yet? */
  hasPrompted: boolean;
  /** 2026-05-27 (v9.2) – true when the signed-in user is already on
   *  the waitlist. Used by ChatDrawer to suppress the post-3rd-answer
   *  nudge ("asking for waitlist even when joined" feedback). */
  waitlistJoined: boolean;
  identify: (email: string) => Promise<{ ok: boolean; error?: string }>;
  signOut: () => Promise<void>;
  /** Mark the first-visit prompt seen without signing in. */
  dismissPrompt: () => void;
  /** Bump local count after a successful chat send. */
  bumpQueryCount: () => void;
  /** Called after a successful waitlist signup so future renders
   *  immediately suppress the nudge without needing /me to refetch. */
  markWaitlistJoined: () => void;
  /** 2026-05-28 – paired with markWaitlistJoined for the chat-drawer
   *  transparent opt-out flow (silent auto-enrol via /identify, then
   *  customer can leave the list via the confirmation card after the
   *  3rd Owl answer). Flipping the local flag back to false avoids
   *  re-showing the "you're on the list" confirmation. */
  markWaitlistLeft: () => void;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [email, setEmail] = useState<string | null>(null);
  const [queryCount, setQueryCount] = useState(0);
  const [hasPrompted, setHasPrompted] = useState(hasBeenPrompted());
  const [waitlistJoined, setWaitlistJoined] = useState(false);

  // On mount: rehydrate from localStorage + verify with /me.
  useEffect(() => {
    let cancelled = false;
    const stored = readSession();
    if (!stored) {
      if (!cancelled) setStatus("anonymous");
      return () => {
        cancelled = true;
      };
    }
    OwlApi.me()
      .then((me) => {
        if (cancelled) return;
        if (me.signed_in && me.email) {
          setEmail(me.email);
          setQueryCount(me.query_count ?? 0);
          setWaitlistJoined(Boolean(me.waitlist_joined));
          setStatus("signed_in");
        } else {
          // Stored token didn't resolve – wipe and fall back.
          clearSession();
          setStatus("anonymous");
        }
      })
      .catch(() => {
        if (cancelled) return;
        // API unreachable – keep the local token, present as anonymous
        // so the page still renders. We'll re-verify on next call.
        setStatus("anonymous");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const identify = useCallback(async (raw: string) => {
    const email = raw.trim().toLowerCase();
    try {
      const res = await OwlApi.identify(email);
      const sess: OwlSession = { token: res.token, email: res.email };
      writeSession(sess);
      setEmail(res.email);
      setQueryCount(res.query_count);
      setStatus("signed_in");
      markPrompted();
      setHasPrompted(true);
      // 2026-05-27 (v9.2) – after identify, check if this email is
      // already on the waitlist from a prior visit. Best-effort –
      // a failure here just leaves the flag false and the user might
      // see one extra nudge, which is the safer default.
      OwlApi.me()
        .then((me) => setWaitlistJoined(Boolean(me.waitlist_joined)))
        .catch(() => { /* harmless */ });
      return { ok: true };
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Couldn't sign you in.";
      return { ok: false, error: msg };
    }
  }, []);

  const signOut = useCallback(async () => {
    await OwlApi.signOut(); // clears storage on its own
    setEmail(null);
    setQueryCount(0);
    setWaitlistJoined(false);
    setStatus("anonymous");
  }, []);

  const dismissPrompt = useCallback(() => {
    markPrompted();
    setHasPrompted(true);
  }, []);

  const bumpQueryCount = useCallback(() => {
    setQueryCount((c) => c + 1);
  }, []);

  const markWaitlistJoined = useCallback(() => {
    setWaitlistJoined(true);
  }, []);

  const markWaitlistLeft = useCallback(() => {
    setWaitlistJoined(false);
  }, []);

  const value = useMemo<SessionState>(
    () => ({
      status,
      email,
      queryCount,
      hasPrompted,
      waitlistJoined,
      identify,
      signOut,
      dismissPrompt,
      bumpQueryCount,
      markWaitlistJoined,
      markWaitlistLeft,
    }),
    [status, email, queryCount, hasPrompted, waitlistJoined,
     identify, signOut, dismissPrompt, bumpQueryCount,
     markWaitlistJoined, markWaitlistLeft],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error("useSession must be used inside <SessionProvider>");
  }
  return v;
}
